import type { MetaFunction } from "react-router";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";
import { ArrowRight } from "../components/marketing/icons";
import { SITE_ORIGIN } from "../brand";

const APP_STORE_URL = "https://apps.shopify.com/quote-desk";
const PAGE_URL = `${SITE_ORIGIN}/compare`;

export const links = marketingLinks;

export const meta: MetaFunction = () => [
  { title: "AI B2B Quote Desk vs Shopify Plus B2B — Honest Comparison" },
  {
    name: "description",
    content:
      "Compare Quote Desk with Shopify Plus B2B for negotiated quotes, buyer links, draft-order acceptance, PO parsing, and counter-offer workflows.",
  },
  {
    name: "keywords",
    content:
      "Shopify Plus B2B alternative, B2B Shopify without Plus, draftOrder workflow Shopify, PO PDF Shopify, Shopify wholesale apps comparison",
  },
  { property: "og:title", content: "AI B2B Quote Desk vs Shopify Plus B2B — Comparison" },
  {
    property: "og:description",
    content: "The B2B quote workflow at 1/30th the price of Shopify Plus — for merchants who don't need Plus's full wholesale storefront.",
  },
  { property: "og:type", content: "website" },
  { property: "og:url", content: PAGE_URL },
  ...socialImageMeta(),
  { tagName: "link", rel: "canonical", href: PAGE_URL },
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What's the cheapest alternative to Shopify Plus for B2B quotes?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Shopify Plus B2B costs $2,300/month minimum. The cheapest alternatives that deliver the same core B2B quote workflow (negotiated per-quote pricing + public quote links + draft-order-to-real-order accept flow) are third-party Shopify apps on Basic, Grow, or Advanced tier — typically $29-79/month. AI B2B Quote Desk is at the low end of that range with Starter $29/mo, Pro $49/mo, Growth $79/mo. Other comparable apps (B2B Wholesale Club, SparkLayer, BSS Commerce B2B) range from $49-249/month.",
          },
        },
        {
          "@type": "Question",
          name: "Should I upgrade to Shopify Plus or use a third-party B2B app?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Depends on your B2B revenue scale and your need for non-quote features. Shopify Plus B2B is the right pick when: you're doing >$5M/year in B2B revenue (the Plus base price gets diluted across volume), you need a fully separate wholesale storefront with branded checkout, you need Shopify's checkout customization beyond what apps allow, you need 200+ checkout customization for compliance. A third-party app is the right pick when: you're doing <$5M/year in B2B, the quote workflow is your main B2B surface, you don't need a separate wholesale storefront, you want to keep your current Shopify tier.",
          },
        },
        {
          "@type": "Question",
          name: "How does AI B2B Quote Desk compare to B2B Wholesale Club?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "B2B Wholesale Club is a long-running app with $19-49/month tiers focusing on wholesale pricing + bulk ordering + customer accounts. Quote Desk focuses on the QUOTE WORKFLOW specifically — quotes go out, customers accept via public link, and the AI counter-offer engine handles negotiations. Quote Desk has PO PDF parsing which B2B Wholesale Club doesn't. B2B Wholesale Club has stronger storefront-facing wholesale features (B2B-only product visibility, quantity break tables on PDPs) which Quote Desk doesn't. Use B2B Wholesale Club if your B2B is browse-and-checkout. Use Quote Desk if your B2B is PO-and-quote.",
          },
        },
        {
          "@type": "Question",
          name: "How does AI B2B Quote Desk compare to SparkLayer?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "SparkLayer is a more comprehensive B2B platform layered on top of Shopify — wholesale storefronts, customer portals, repeat ordering, customer-specific catalogs. It starts at ~$49/month for basic features and ramps to $199+ for full functionality. Quote Desk is more focused: the quote workflow + PO parsing + AI counter-offers, no separate wholesale storefront. If you want a full B2B portal experience for your customers, SparkLayer. If you want the quote workflow as a focused add-on at a lower price, Quote Desk.",
          },
        },
        {
          "@type": "Question",
          name: "Can I use Quote Desk AND a wholesale storefront app together?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Quote Desk handles the quote-and-accept workflow; a wholesale storefront app (B2B Wholesale Club, BSS Commerce B2B, SparkLayer's basic tier) handles the browse-and-checkout workflow. The two solve non-overlapping problems. Merchants doing both PO-driven B2B (quotes) AND catalog-driven B2B (wholesale browse) often run both.",
          },
        },
        {
          "@type": "Question",
          name: "Is the AI counter-offer engine worth the Pro upgrade?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "If you negotiate on more than 5-10 quotes per month, yes. The engine reads your historical pricing for the customer + your margin floor + typical negotiation patterns, and suggests a counter-counter in seconds. That's seconds vs the 5-10 minutes it takes to manually compute a counter while consulting your pricing history. At 10 quotes/month, the time savings alone (50+ minutes) is worth the $20 Pro→Starter delta. Plus you get PO PDF parsing which adds 10-20 minutes saved per inbound PO.",
          },
        },
      ],
    },
  },
];

interface Row {
  capability: string;
  quoteDesk: string;
  shopifyPlus: string;
  thirdParty: string;
  us?: "yes";
}

const COMPARISON: Row[] = [
  {
    capability: "Entry price",
    quoteDesk: "Starter $29/mo · 14-day trial",
    shopifyPlus: "$2,300/mo minimum",
    thirdParty: "$19–79/mo typical",
    us: "yes",
  },
  {
    capability: "Negotiated per-quote pricing",
    quoteDesk: "Yes — per line, per quote",
    shopifyPlus: "Yes — integrated with Plus catalogs",
    thirdParty: "Varies — most do basic per-customer pricing",
  },
  {
    capability: "Public quote links (no buyer login)",
    quoteDesk: "Yes — /q/<token> opaque links",
    shopifyPlus: "Yes — built into Plus B2B",
    thirdParty: "Rare — most require customer accounts",
    us: "yes",
  },
  {
    capability: "Buyer accepts → real Shopify order",
    quoteDesk: "Yes — via draftOrderCreate",
    shopifyPlus: "Yes — native draft order flow",
    thirdParty: "Some — usually draftOrderCreate or Buy Button",
  },
  {
    capability: "PO PDF parsing",
    quoteDesk: "Yes (Pro) — confidence-scored matching",
    shopifyPlus: "No — not in the Plus B2B suite",
    thirdParty: "Rare — usually a separate dedicated app",
    us: "yes",
  },
  {
    capability: "AI counter-offer engine",
    quoteDesk: "Yes (Growth) — margin-aware, you approve",
    shopifyPlus: "No",
    thirdParty: "No — Quote Desk is the only one currently",
    us: "yes",
  },
  {
    capability: "Wholesale storefront / B2B-only visibility",
    quoteDesk: "No — focused on the quote workflow",
    shopifyPlus: "Yes — full wholesale storefront",
    thirdParty: "Some — SparkLayer, B2B Wholesale Club",
  },
  {
    capability: "Customer portal / repeat ordering",
    quoteDesk: "No",
    shopifyPlus: "Yes",
    thirdParty: "Some — SparkLayer, BSS Commerce B2B",
  },
  {
    capability: "Quote ledger & reversibility",
    quoteDesk: "Yes — full audit log, cancellable",
    shopifyPlus: "Partial — Plus admin has some history",
    thirdParty: "Varies",
    us: "yes",
  },
];

const FITS = [
  {
    head: "When Quote Desk is the right pick",
    items: [
      "You're on Shopify Basic, Grow, or Advanced and don't want to pay for Plus.",
      "Your B2B is PO-driven — buyers send POs, you reply with quotes, they accept.",
      "You need the quote workflow specifically (negotiated pricing → quote link → accept → real order).",
      "You want PO PDF parsing to cut a 15-minute PO-by-email into a 60-second flow.",
      "You negotiate on 5–10+ quotes a month and the AI counter-offer engine saves you time.",
      "You're doing under $5M/year in B2B revenue.",
    ],
  },
  {
    head: "When Shopify Plus B2B is the better pick",
    items: [
      "You're doing >$5M/year in B2B and Plus's base price gets diluted across volume.",
      "You need a fully separate wholesale storefront with branded checkout.",
      "You need Shopify's deeper checkout customization (200+ Plus-only customizations).",
      "You need a customer portal with repeat-ordering history.",
      "You need wholesale-only product visibility.",
    ],
  },
  {
    head: "When a wholesale-storefront app is the better pick",
    items: [
      "Your B2B is browse-and-checkout, not PO-and-quote.",
      "You don't need PO PDF parsing or AI counter-offers.",
      "You want a customer portal with repeat-ordering history.",
      "B2B Wholesale Club, SparkLayer, and BSS Commerce B2B all do this well at $19–199/mo.",
    ],
  },
];

export default function ComparePage() {
  return (
    <MarketingShell>
      <section className="qd-hero" style={{ paddingBottom: "1rem" }}>
        <div className="qd-wrap">
          <div className="qd-head qd-head--center qd-reveal" data-d="1">
            <p className="qd-eyebrow qd-eyebrow--center">Honest comparison</p>
            <h2 style={{ fontSize: "var(--step-4)" }}>
              Quote Desk vs Shopify Plus B2B vs other B2B apps
            </h2>
            <p>
              Shopify Plus B2B is excellent above $5M/year in B2B revenue with
              the full wholesale storefront. Quote Desk delivers the quote
              workflow specifically at a thirtieth of the price — for merchants
              on Basic, Grow, and Advanced who don't need Plus's broader suite.
            </p>
          </div>
          <div
            className="qd-hero__cta"
            style={{ justifyContent: "center", marginTop: "1.8rem" }}
          >
            <a className="qd-btn" href={APP_STORE_URL}>
              See it on the App Store
              <ArrowRight />
            </a>
            <a className="qd-btn qd-btn--ghost" href="/pricing">
              See pricing
            </a>
          </div>
        </div>
      </section>

      <section className="qd-section--tight">
        <div className="qd-wrap">
          <div className="qd-tablewrap qd-reveal" data-d="1">
            <table className="qd-table">
              <caption className="qd-sr-only" style={{ position: "absolute", left: "-9999px" }}>
                Capability comparison between Quote Desk, Shopify Plus B2B, and other B2B apps
              </caption>
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col" className="is-us">
                    Quote Desk
                  </th>
                  <th scope="col">Shopify Plus B2B</th>
                  <th scope="col">Other B2B apps</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.capability}>
                    <th scope="row">{row.capability}</th>
                    <td className="is-us">{row.quoteDesk}</td>
                    <td>{row.shopifyPlus}</td>
                    <td>{row.thirdParty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p
            style={{
              fontSize: "0.86rem",
              color: "var(--ink-500)",
              marginTop: "1rem",
              fontStyle: "italic",
              maxWidth: "44rem",
            }}
          >
            Pricing and feature data current as of 2026-06-06. Shopify Plus
            minimum pricing fluctuates with merchant negotiations and GMV; the
            $2,300/mo figure is the documented public floor. Third-party app
            pricing changes — always verify on the competitor's own Shopify App
            Store listing before deciding.
          </p>
        </div>
      </section>

      <section className="qd-section qd-band">
        <div className="qd-wrap qd-wrap--narrow">
          <div className="qd-head qd-reveal" data-d="1">
            <p className="qd-eyebrow">Where each one fits</p>
            <h2>Pick by your B2B shape, not by the logo</h2>
          </div>
          <div style={{ display: "grid", gap: "1.6rem", marginTop: "2rem" }}>
            {FITS.map((f) => (
              <div className="qd-card" key={f.head} style={{ padding: "1.7rem 1.7rem" }}>
                <h3 style={{ fontSize: "1.25rem" }}>{f.head}</h3>
                <ul
                  className="qd-prose"
                  style={{ marginTop: "1rem", paddingLeft: "1.2rem", display: "grid", gap: "0.5rem" }}
                >
                  {f.items.map((it) => (
                    <li key={it} style={{ fontSize: "1rem" }}>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="qd-callout">
            <p>
              <strong>Running both is common.</strong> Merchants doing
              PO-driven B2B (quotes) and catalog-driven B2B (wholesale browse)
              often pair Quote Desk for the quote-and-accept workflow with a
              wholesale storefront app for browse-and-checkout. The two solve
              non-overlapping problems.
            </p>
          </div>
        </div>
      </section>

      <section className="qd-section">
        <div className="qd-wrap">
          <div className="qd-cta qd-reveal" data-d="1">
            <h2>The B2B quote workflow at 1/30th the price of Plus</h2>
            <p>
              Quote Desk is available on the Shopify App Store now. Every plan
              ships with a 14-day free trial.
            </p>
            <div className="qd-cta__actions">
              <a className="qd-btn" href={APP_STORE_URL}>
                Visit the App Store listing
                <ArrowRight />
              </a>
              <a className="qd-btn qd-btn--ghost" href="/features">
                Full feature list
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
